import { ExtendedPlatformContext } from '../../../../../types/platformApi';
import {
  AddToStageData,
  AppData,
  UnifiedWidget,
  WidgetAppearanceDescriptor,
} from '../../../../../types/unifiedComponents';
import {
  AddUnifiedComponentsErrorCode as ErrorCode,
  createAddUnifiedComponentsError,
} from '../errors';
import {
  CompRef,
  ComponentLayoutObject,
  ComponentLayout,
  Point,
  WidgetDescriptor,
  CompStructure,
  CompLayout,
} from '@wix/document-services-types';
import { getAppDescriptorByAppData } from '@wix/blocks-widget-services/editor';
import { addBuilderWidgetComponent } from './addBuilderComponent';
import {
  cleanPresetIdPrefix,
  getFixedPosition,
  getSizeValue,
  getUnitSize,
} from '../utils';
import { MainPresets } from '@wix/ambassador-devcenter-components-v1-component/types';
import { WidgetDimensions } from '@wix/editor-platform-sdk-types';
import { HttpClient } from '@wix/http-client';

const httpClient = new HttpClient();
const appDescriptors: Record<
  string,
  Awaited<ReturnType<typeof getAppDescriptorByAppData>>
> = {};

const TOKEN = 'unified-components-installation';

export async function getBlocksInstallationSizeResolver(
  appData: AppData,
  widgetDefinition: UnifiedWidget,
  preset?: MainPresets,
) {
  const { presets, defaultSize } = await getWidgetDescriptor(
    appData,
    widgetDefinition,
  );

  const selectedPresetId =
    preset?.desktopPresetId ??
    widgetDefinition.installation?.widget?.defaultPreset?.desktopPresetId;
  const { size } = getPresetAndSize(
    widgetDefinition.widgetId,
    widgetDefinition.isStretched?.desktop,
    { presets, defaultSize },
    selectedPresetId,
  );
  return { width: size.width, height: size.height };
}

export function getPresetAndSize(
  widgetId: string,
  isStretched: boolean,
  { presets = [], defaultSize }: WidgetAppearanceDescriptor,
  presetId?: string,
  mobilePresetId?: string,
) {
  const preset =
    presets.find(
      (widgetPreset) => cleanPresetIdPrefix(widgetPreset.presetId) === presetId,
    ) ?? presets[0];
  if (!preset) {
    throw createAddUnifiedComponentsError(
      ErrorCode.addingBlocksWidgetFailed,
      'Could not fetch preset for blocks widget',
    ).withUserComponentId(widgetId, 'unified-widget');
  }

  const size = (preset.defaultSize as WidgetDimensions) ?? defaultSize;
  if (!size) {
    throw createAddUnifiedComponentsError(
      ErrorCode.addingBlocksWidgetFailed,
      'Could not fetch default size for blocks widget',
    ).withUserComponentId(widgetId, 'unified-widget');
  }

  const mobilePreset =
    presets.find(
      (widgetPreset) =>
        cleanPresetIdPrefix(widgetPreset.presetId) === mobilePresetId,
    ) ?? preset;
  const mobilePresetSize =
    (mobilePreset.defaultSize as WidgetDimensions) ?? size;

  return {
    size: {
      height: getSizeValue(size.height),
      width: getSizeValue(size.width),
      ...(isStretched
        ? {
            docked: {
              left: { px: 0, vw: 0 },
              right: { px: 0, vw: 0 },
            },
          }
        : {}),
    },
    presetId: cleanPresetIdPrefix(preset.presetId),
    mobile: {
      id: cleanPresetIdPrefix(mobilePresetId ?? mobilePreset.presetId),
      size: {
        height: getSizeValue(mobilePresetSize.height),
        width: getSizeValue(mobilePresetSize.width),
      },
    },
  };
}

export function createLayouts(
  componentLayout: ComponentLayout,
): Partial<ComponentLayoutObject> {
  return {
    componentLayout,
    containerLayout: {
      type: 'GridContainerLayout',
      rows: [{ type: 'fr', value: 1 }],
      columns: [{ type: 'fr', value: 1 }],
    },
    itemLayout: {
      id: '',
      type: 'GridItemLayout',
      gridArea: {
        columnStart: 1,
        columnEnd: 2,
        rowStart: 1,
        rowEnd: 2,
      },
      alignSelf: 'center',
      justifySelf: 'center',
      margins: {
        left: { type: 'percentage', value: 0 },
        top: { type: 'px', value: 0 },
      },
    },
    type: 'SingleLayoutData',
  };
}

export function getLayouts(
  size: WidgetDimensions,
  isStretched: boolean = false,
): Partial<ComponentLayoutObject> {
  const componentLayout: ComponentLayout = isStretched
    ? {
        type: 'ComponentLayout',
        height: { type: 'percentage', value: 100 },
        width: { type: 'percentage', value: 100 },
      }
    : {
        type: 'ComponentLayout',
        width: size.width,
        height: { type: 'auto' },
        minWidth: size.width,
        minHeight: size.height,
      };

  return createLayouts(componentLayout);
}

export function getWidgetAppearanceData({
  widgetDefinition,
  widgetDescriptor,
  containerLayout,
  presetId,
  mobilePresetId,
  position,
  isStretched = false,
}: {
  widgetDefinition: UnifiedWidget;
  widgetDescriptor: WidgetAppearanceDescriptor;
  containerLayout: CompLayout | null;
  presetId?: string;
  mobilePresetId?: string;
  position?: Point;
  isStretched?: boolean;
}) {
  const selectedPresetId =
    presetId ??
    widgetDefinition.installation?.widget?.defaultPreset?.desktopPresetId;

  const {
    mobile,
    size,
    presetId: addedPresetId,
  } = getPresetAndSize(
    widgetDefinition.widgetId,
    isStretched,
    widgetDescriptor,
    selectedPresetId,
    mobilePresetId,
  );

  const layout = {
    ...size,
    ...getFixedPosition(containerLayout, size, position),
  };
  const layouts = getLayouts(
    {
      width: getUnitSize(size.width),
      height: getUnitSize(size.height),
    },
    isStretched,
  );

  return {
    presetId: addedPresetId,
    size,
    mobile,
    layout,
    layouts,
  };
}

export function getBlocksStructure(
  context: ExtendedPlatformContext,
  appDefinitionId: string,
  widgetDefinition: UnifiedWidget,
  presetId: string,
  widgetDescriptor: WidgetAppearanceDescriptor,
  containerLayout: CompLayout | null,
  position?: Point,
  mobilePresetId?: string,
): CompStructure {
  const isStudio = window.commonConfig?.host?.toLowerCase() === 'studio';
  const widgetAppearanceData = getWidgetAppearanceData({
    widgetDefinition,
    widgetDescriptor,
    containerLayout,
    presetId,
    mobilePresetId,
    position,
  });
  const presets = {
    layout: widgetAppearanceData.presetId,
    style: widgetAppearanceData.presetId,
  };

  // TODO: [EP-6783] Get an API from the Editors to get the mobile breakpoint ID
  const findMobileBreakpointId = () => {
    const pageRef = context.documentServices.pages.getCurrentPage();
    const breakpoints =
      context.documentServices.breakpoints.get(pageRef).values;
    return breakpoints.reduce((prev, curr) =>
      prev.canvasSize! < curr.canvasSize! ? prev : curr,
    ).id!;
  };

  const scopedPresetsMobileKey = isStudio
    ? findMobileBreakpointId()
    : 'MOBILE-VARIANT';

  const mobileScopedPreset = {
    layout: widgetAppearanceData.mobile.id,
    style: widgetAppearanceData.mobile.id,
  };

  const mobileHints = {
    type: 'MobileHints',
    recommendedHeight: widgetAppearanceData.mobile.size.height,
    recommendedWidth: widgetAppearanceData.mobile.size.width,
    props: {
      minHeight: widgetAppearanceData.mobile.size.height,
      type: 'RefComponentProperties',
    },
  };

  // TODO: [WBL-9453] returned type in DM for buildRefWidgetStructure is void
  // @ts-expect-error
  const structure: CompStructure =
    context.documentServices.appStudioWidgets.buildRefWidgetStructure(
      appDefinitionId,
      widgetDefinition.widgetId,
      {
        // TODO: [WBL-9453] type of options is not updated for buildRefWidgetStructure in DM
        // @ts-expect-error
        presets,
        scopedPresets: {
          ...(mobilePresetId
            ? { [scopedPresetsMobileKey]: mobileScopedPreset }
            : {}),
        },
        ...(isStudio
          ? { layouts: widgetAppearanceData.layouts }
          : {
              layout: widgetAppearanceData.layout,
              props: {
                type: 'RefComponentProperties',
                minHeight: widgetAppearanceData.layout.height,
              },
              ...(mobilePresetId
                ? {
                    mobileHints,
                  }
                : {}),
            }),
      },
    );

  if (structure.layouts) {
    structure.layoutResponsive = {
      id: 'FAKE',
      type: 'LayoutData',
      componentLayouts: [
        ...(structure.layouts.componentLayout
          ? [structure.layouts.componentLayout]
          : []),
      ],
      containerLayouts: [
        ...(structure.layouts.containerLayout
          ? [structure.layouts.containerLayout]
          : []),
      ],
      itemLayouts: [
        ...(structure.layouts.itemLayout ? [structure.layouts.itemLayout] : []),
      ],
    } as CompStructure['layoutResponsive'];
  }

  return structure;
}

async function addBlocksClosedWidget(
  context: ExtendedPlatformContext,
  appData: AppData,
  widgetDescriptor: WidgetDescriptor,
  widgetDefinition: UnifiedWidget,
  containerRef: CompRef,
  addToStageData: AddToStageData,
): Promise<CompRef> {
  const { widgetId } = widgetDefinition;
  const isStretched = !!widgetDefinition.isStretched?.desktop;
  const containerLayout =
    context.documentServices.components.layout.get(containerRef);
  const { size, presetId, mobile, layout, layouts } = getWidgetAppearanceData({
    widgetDefinition,
    widgetDescriptor,
    containerLayout,
    presetId: addToStageData.presetId,
    mobilePresetId: addToStageData.mobilePresetId,
    position: addToStageData.position,
    isStretched,
  });

  return new Promise<CompRef>((resolve, reject) =>
    // TODO: [EP-5207] Use getBlocksStructure function and replace API used to add blocks widgets to the
    //  general DM components API (context.platformApiMethods.document.components.add)
    context.platformApiMethods.application.appStudioWidgets
      .addWidget(appData, TOKEN, {
        widgetId,
        installationType: 'closed',
        presetIds: presetId
          ? {
              layout: presetId,
              style: presetId,
            }
          : undefined,
        layout,
        layouts,
        containerRef,
        scopedPresets: {
          ...(addToStageData.mobilePresetId
            ? {
                mobile: {
                  layout: mobile.id,
                  style: mobile.id,
                },
              }
            : {}),
        },
        dimensionsByDevice: {
          desktop: {
            height: getUnitSize(size.height),
            width: getUnitSize(size.width),
          },
          mobile: {
            height: getUnitSize(mobile.size.height),
            width: getUnitSize(mobile.size.width),
          },
        },
      })
      .then(resolve)
      .catch((error: Error) =>
        reject(
          createAddUnifiedComponentsError(
            ErrorCode.addingBlocksWidgetFailed,
            'Could not add blocks widget',
          )
            .withUserComponentId(widgetId, 'unified-widget')
            .withParentError(error),
        ),
      ),
  );
}

async function getWidgetDescriptor(
  appData: AppData,
  widgetDefinition: UnifiedWidget,
) {
  if (!appDescriptors[appData.appDefinitionId]) {
    appDescriptors[appData.appDefinitionId] = await getAppDescriptorByAppData(
      appData,
      { httpClient },
    );
  }
  const appDescriptor = appDescriptors[appData.appDefinitionId];
  const widgetDescriptor = Object.values(appDescriptor?.widgets ?? {}).find(
    ({ devCenterWidgetId }) => devCenterWidgetId === widgetDefinition.widgetId,
  );
  if (!widgetDescriptor) {
    throw createAddUnifiedComponentsError(
      ErrorCode.addingBlocksWidgetFailed,
      'Could not fetch widget from app descriptor',
    ).withUserComponentId(widgetDefinition.widgetId, 'unified-widget');
  }
  return widgetDescriptor;
}

export async function addBlocksWidgetResolver(
  context: ExtendedPlatformContext,
  appData: AppData,
  widgetDefinition: UnifiedWidget,
  containerRef: CompRef,
  addToStageData: AddToStageData,
): Promise<CompRef> {
  const widgetDescriptor = await getWidgetDescriptor(appData, widgetDefinition);
  if (widgetDefinition.componentModel) {
    return addBuilderWidgetComponent(
      context,
      appData,
      widgetDescriptor,
      widgetDefinition,
      containerRef,
      addToStageData,
    );
  }

  return addBlocksClosedWidget(
    context,
    appData,
    widgetDescriptor,
    widgetDefinition,
    containerRef,
    addToStageData,
  );
}
